import { StateManager } from '../../managers';
import { getFullSpriteUrl } from '../../combat/spriteConfig';
import { rarityColor } from './TooltipContent';
import { formatUnitClass, formatElement } from './GameTerms';
import { elementColor } from '../../utils/elementColors';
import { getAbilityIconUrl } from './ItemIcon';
import type { PartyUnit, ConfigData } from '../../types';
import { SKILL_SLOT_KEYS } from '../../types';

export interface RosterPickerOptions {
  /** Header title shown at the top of the panel. */
  title: string;
  /** Pre-filtered list of units to show. Caller decides what's eligible. */
  units: PartyUnit[];
  /** When set, renders a sticky "Pairing with" strip above the filter bar. */
  pairingWith?: PartyUnit | null;
  /** Show element filter row (default: true). */
  elementFilter?: boolean;
  /** Show class filter row (default: false). */
  classFilter?: boolean;
  /** Returns a reason string to disable a unit, or false to enable it. */
  isDisabled?: (unit: PartyUnit) => string | false;
  /** Render the inner card content. Receives the unit + its disabled state. */
  renderCard: (unit: PartyUnit, disabled: string | false) => string;
  /** Optional extra CSS classes for the .roster-card wrapper (e.g. 'roster-card--naked'). */
  cardClasses?: (unit: PartyUnit, disabled: string | false) => string;
  /** Called when a non-disabled card is picked. */
  onPick: (unit: PartyUnit) => void;
  /** Called after the picker closes (via pick or dismiss). */
  onClose?: () => void;
}

let activeOverlay: HTMLElement | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

function close(): void {
  if (!activeOverlay) return;
  const overlay = activeOverlay;
  overlay.classList.remove('is-visible');

  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }

  setTimeout(() => {
    overlay.remove();
    if (activeOverlay === overlay) activeOverlay = null;
  }, 350);
}

function open(options: RosterPickerOptions): void {
  if (activeOverlay) close();

  const {
    title,
    units,
    pairingWith = null,
    elementFilter = true,
    classFilter = false,
    isDisabled,
    renderCard,
    cardClasses,
    onPick,
    onClose,
  } = options;

  const configData = StateManager.get('configData') as ConfigData | undefined;

  // ─── Pairing strip (Soulloom uses this; home does not) ───
  const pairingStripHTML = pairingWith
    ? buildPairingStrip(pairingWith, configData)
    : '';

  // ─── Element filter buttons ───
  const elementButtons = elementFilter
    ? buildElementFilter(units)
    : '';

  // ─── Class filter buttons (Soulloom) ───
  const classButtons = classFilter
    ? buildClassFilter(units)
    : '';

  // ─── Cards ───
  const cardsHTML = units.map(unit => {
    const disabled = isDisabled ? isDisabled(unit) : false;
    const inner = renderCard(unit, disabled);
    const el = (unit.defensiveTypes ?? [])[0] ?? unit.element ?? '';
    const eColor = el ? elementColor(el) : 'rgba(255,255,255,0.3)';
    const disabledCls = disabled ? ' roster-card--disabled' : '';
    const extraCls = cardClasses ? ` ${cardClasses(unit, disabled)}` : '';
    const disabledTitle = typeof disabled === 'string' ? ` title="${disabled}"` : '';
    return `<div class="roster-card${disabledCls}${extraCls}" data-unit-id="${unit.id}" data-element="${el}" data-class="${unit.unitClass}" style="--element-color:${eColor}"${disabledTitle}>${inner}</div>`;
  }).join('');

  const overlay = document.createElement('div');
  overlay.className = 'roster-overlay';
  overlay.innerHTML = `
    <div class="roster-overlay-backdrop roster-backdrop"></div>
    <div class="roster-overlay-panel">
      <div class="roster-overlay-header">
        <span class="roster-overlay-title">${title}</span>
        <button class="roster-close-btn roster-overlay-close" type="button">✕</button>
      </div>
      ${pairingStripHTML}
      ${elementButtons || classButtons ? `
        <div class="roster-filter-bar">
          ${elementButtons}
          ${classButtons}
        </div>
      ` : ''}
      <div class="roster-overlay-grid">
        ${cardsHTML}
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  activeOverlay = overlay;
  requestAnimationFrame(() => overlay.classList.add('is-visible'));

  // ─── Dismiss handlers ───
  const handleClose = () => {
    close();
    onClose?.();
  };

  overlay.querySelector('.roster-close-btn')?.addEventListener('click', handleClose);
  overlay.querySelector('.roster-backdrop')?.addEventListener('click', handleClose);

  escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
  document.addEventListener('keydown', escHandler);

  // ─── Filter buttons (one bar, two filter dimensions) ───
  overlay.querySelectorAll<HTMLButtonElement>('.roster-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const dim = btn.dataset.filterDim ?? 'element';
      // Deactivate siblings of the same dimension only
      overlay.querySelectorAll<HTMLButtonElement>(`.roster-filter-btn[data-filter-dim="${dim}"]`)
        .forEach(b => b.classList.remove('roster-filter-active'));
      btn.classList.add('roster-filter-active');
      applyFilters(overlay);
    });
  });

  // ─── Card clicks ───
  overlay.querySelectorAll<HTMLElement>('.roster-card').forEach(card => {
    card.addEventListener('click', () => {
      if (card.classList.contains('roster-card--disabled')) return;
      const unitId = card.dataset.unitId;
      const unit = units.find(u => u.id === unitId);
      if (!unit) return;
      onPick(unit);
      close();
      onClose?.();
    });
  });
}

function buildPairingStrip(unit: PartyUnit, configData: ConfigData | undefined): string {
  const template = configData?.unitTemplates?.[unit.unitClass];
  const skinKey = unit.activeSkinId && configData?.skins?.[unit.activeSkinId]?.spriteKey;
  const spriteKey = skinKey || template?.spriteKey || unit.unitClass;
  const spriteSrc = getFullSpriteUrl(spriteKey);
  const rColor = rarityColor(unit.rarity);
  const className = formatUnitClass(unit.unitClass);
  return `
    <div class="roster-pairing-strip">
      <span class="roster-pairing-label">Pairing with</span>
      <div class="roster-pairing-card">
        <img class="roster-pairing-sprite" src="${spriteSrc}" alt="${unit.name}" draggable="false">
        <div class="roster-pairing-info">
          <span class="roster-pairing-name" style="color:${rColor}">${unit.name}</span>
          <span class="roster-pairing-meta">Lv.${unit.level} ${className}</span>
        </div>
      </div>
    </div>
  `;
}

function buildElementFilter(units: PartyUnit[]): string {
  const elements = new Set<string>();
  for (const u of units) {
    const el = (u.defensiveTypes ?? [])[0] ?? u.element;
    if (el) elements.add(el);
  }
  const buttons = [...elements].sort().map(el => {
    const eColor = elementColor(el);
    const label = formatElement(el);
    return `<button class="roster-filter-btn" data-filter-dim="element" data-filter-value="${el}" style="--filter-color:${eColor}">${label}</button>`;
  }).join('');
  return `
    <button class="roster-filter-btn roster-filter-active" data-filter-dim="element" data-filter-value="all">All</button>
    ${buttons}
  `;
}

function buildClassFilter(units: PartyUnit[]): string {
  const classes = new Set<string>();
  for (const u of units) classes.add(u.unitClass);
  const buttons = [...classes].sort().map(cls => {
    const label = formatUnitClass(cls);
    return `<button class="roster-filter-btn" data-filter-dim="class" data-filter-value="${cls}">${label}</button>`;
  }).join('');
  return `
    <button class="roster-filter-btn roster-filter-active" data-filter-dim="class" data-filter-value="all">All Classes</button>
    ${buttons}
  `;
}

function applyFilters(overlay: HTMLElement): void {
  const activeElement = overlay.querySelector<HTMLButtonElement>('.roster-filter-btn[data-filter-dim="element"].roster-filter-active')?.dataset.filterValue ?? 'all';
  const activeClass = overlay.querySelector<HTMLButtonElement>('.roster-filter-btn[data-filter-dim="class"].roster-filter-active')?.dataset.filterValue ?? 'all';

  overlay.querySelectorAll<HTMLElement>('.roster-card').forEach(card => {
    const cardEl = card.dataset.element ?? '';
    const cardCls = card.dataset.class ?? '';
    const passElement = activeElement === 'all' || cardEl === activeElement;
    const passClass = activeClass === 'all' || cardCls === activeClass;
    card.style.display = (passElement && passClass) ? '' : 'none';
  });
}

// ─── Built-in card renderer for the home/main-menu picker. ───
// Mirrors the previous in-place markup (skill icons + naked warning).
export function renderHomePickerCard(unit: PartyUnit, configData: ConfigData | undefined): string {
  const abilities = configData?.abilities ?? {};
  const template = configData?.unitTemplates?.[unit.unitClass];
  const skinKey = unit.activeSkinId && configData?.skins?.[unit.activeSkinId]?.spriteKey;
  const spriteKey = skinKey || template?.spriteKey || unit.unitClass;
  const spriteSrc = getFullSpriteUrl(spriteKey);
  const hasSkills = SKILL_SLOT_KEYS.some(key => unit[key]);
  const className = formatUnitClass(unit.unitClass);
  const rColor = rarityColor(unit.rarity);

  const skillIds = SKILL_SLOT_KEYS.map(key => unit[key]).filter(Boolean) as string[];
  const skillIcons = skillIds.map(id => {
    const iconUrl = getAbilityIconUrl(id);
    return iconUrl
      ? `<img class="roster-skill-icon" src="${iconUrl}" alt="${abilities[id]?.name ?? ''}" title="${abilities[id]?.name ?? ''}" />`
      : '';
  }).join('');

  return `
    ${!hasSkills ? '<span class="roster-card-warn">!</span>' : ''}
    <div class="roster-card-art">
      <img class="roster-card-sprite" src="${spriteSrc}" alt="${unit.name}" draggable="false">
    </div>
    <div class="roster-card-info">
      ${skillIcons ? `<div class="roster-card-skills">${skillIcons}</div>` : ''}
      <span class="roster-card-name" style="color:${rColor}">${unit.name}</span>
      <span class="roster-card-meta">Lv.${unit.level} ${className}</span>
    </div>
  `;
}

export const RosterPicker = { open, close };
